import React from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { Calendar, CreditCard, AlertTriangle, Lock, Unlock, Clock, CheckCircle2 } from 'lucide-react';

const VatModernHeader = ({
    vatPeriod,
    totalVat,
    undoingClose,
    handleUndoClose
}) => {
    // Use calculated totalVat if available (even if 0), otherwise fall back to stored vat_total
    const displayAmount = totalVat !== undefined
        ? totalVat
        : (vatPeriod?.vat_total || 0);
    const formatCurrency = (amount) => {
        const numAmount = Number(amount) || 0;
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numAmount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('sv-SE');
    };

    const getStatusConfig = () => {
        if (vatPeriod?.state === 'closed') {
            return {
                icon: Lock,
                text: 'Stängd period',
                bgColor: '#fee2e2',
                textColor: '#dc2626',
                borderColor: '#fecaca'
            };
        }

        return {
            icon: Unlock,
            text: 'Öppen period',
            bgColor: '#f0fdf4',
            textColor: '#166534',
            borderColor: '#bbf7d0'
        };
    };

    const isOverdue = () => {
        if (!vatPeriod || vatPeriod.state !== 'open') return false;
        const today = new Date();
        const dueDate = new Date(vatPeriod.due_date);
        return dueDate < today;
    };

    const statusConfig = getStatusConfig();
    const StatusIcon = statusConfig.icon;

    return (
        <>
            <style>{`
                .vat-header {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    color: #1e293b;
                }
                
                .vat-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #1e293b;
                }
                
                .vat-subtitle {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-bottom: 1rem;
                }
                
                .vat-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                    margin-top: 1rem;
                }
                
                .vat-stat-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 0.75rem;
                }
                
                .vat-stat-label {
                    font-size: 0.75rem;
                    color: #64748b;
                    margin-bottom: 0.25rem;
                    text-transform: uppercase;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                }
                
                .vat-stat-value {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .vat-header-actions {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                    flex-wrap: wrap;
                }
                
                .vat-status-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.375rem 0.75rem;
                    border-radius: 16px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .vat-header-meta {
                    display: flex;
                    gap: 1rem;
                    align-items: center;
                    flex-wrap: wrap;
                    margin-bottom: 1rem;
                }
                
                .vat-due-date-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.375rem 0.75rem;
                    border-radius: 16px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    background: #eff6ff;
                    color: #1d4ed8;
                    border: 1px solid #bfdbfe;
                }
                
                .vat-overdue-indicator {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                
                /* Mobile responsive */
                @media (max-width: 768px) {
                    .vat-header {
                        padding: 1rem;
                    }
                    
                    .vat-title {
                        font-size: 1.25rem;
                    }
                    
                    .vat-stats {
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 0.75rem;
                    }
                    
                    .vat-header-meta {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.75rem;
                    }
                }
            `}</style>

            <div className="vat-header">
                <div className="d-flex justify-content-between align-items-start">
                    <div style={{ flex: 1 }}>
                        <h1 className="vat-title">
                            <Calendar size={24} color="#3b82f6" />
                            Momsrapport för perioden: {vatPeriod && formatDate(vatPeriod.start_date)} - {vatPeriod && formatDate(vatPeriod.end_date)}
                        </h1>
                        <p className="vat-subtitle">
                            Hantera momsrapportering för denna period
                        </p>

                        <div className="vat-header-meta">
                            <div
                                className="vat-status-indicator"
                                style={{
                                    backgroundColor: statusConfig.bgColor,
                                    color: statusConfig.textColor,
                                    border: `1px solid ${statusConfig.borderColor}`
                                }}
                            >
                                <StatusIcon size={14} />
                                {statusConfig.text}
                            </div>

                            <div className={`vat-due-date-indicator ${isOverdue() ? 'vat-overdue-indicator' : ''}`}>
                                {isOverdue() ? <AlertTriangle size={14} /> : <Clock size={14} />}
                                Senast: {vatPeriod && formatDate(vatPeriod.due_date)}
                                {isOverdue() && ' (FÖRSENAD!)'}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="vat-stats">
                    <div className="vat-stat-card">
                        <div className="vat-stat-label">Moms att betala/få tillbaka</div>
                        <div
                            className="vat-stat-value"
                            style={{
                                color: displayAmount > 0 ? '#dc2626' : '#059669'
                            }}
                        >
                            {formatCurrency(displayAmount)}
                        </div>
                    </div>

                    <div className="vat-stat-card">
                        <div className="vat-stat-label">Periodtyp</div>
                        <div className="vat-stat-value">
                            {vatPeriod?.period_type === 'monthly' ? 'Månadsvis' : 'Kvartalsvis'}
                        </div>
                    </div>

                    <div className="vat-stat-card">
                        <div className="vat-stat-label">Status</div>
                        <div className="vat-stat-value">
                            {vatPeriod?.state === 'closed' ? 'Stängd' :
                                vatPeriod?.state === 'submitted' ? 'Inlämnad' : 'Öppen'}
                        </div>
                    </div>

                    <div className="vat-stat-card">
                        <div className="vat-stat-label">Aktuellt steg</div>
                        <div className="vat-stat-value">
                            {vatPeriod?.current_step ?
                                vatPeriod.current_step.charAt(0).toUpperCase() + vatPeriod.current_step.slice(1)
                                : 'Instruktioner'}
                        </div>
                    </div>
                </div>

                {vatPeriod && vatPeriod.state === 'closed' && (
                    <Alert variant="warning" className="d-flex align-items-center mt-3">
                        <AlertTriangle size={20} className="me-2" />
                        <div className="flex-grow-1">
                            Denna momsperiod är stängd.
                            <Button variant="link" onClick={handleUndoClose} disabled={undoingClose} className="p-0 ms-2">
                                Ångra stängning
                            </Button>
                            {undoingClose && <Spinner animation="border" size="sm" role="status" aria-hidden="true" className="ms-2" />}
                        </div>
                    </Alert>
                )}
            </div>
        </>
    );
};

export default VatModernHeader;